/**
 * 🧭 SECTEURS D'ACTIVITÉ (source unique)
 * Chaque secteur réunit une entreprise à financer (label), son pictogramme (icon) et la raison
 * d'y investir (impact).
 * Utilisé par le catalogue (data/projects.js), les filtres, les cartes de secteur de la page Projets
 * et le formulaire « Présenter mon projet ».
 */

import {
  IconSeedling, IconIndustry, IconUtensils, IconTools, IconTshirt, IconStore, IconWrench, IconHardHat, IconSolarPanel, IconTint, IconRecycle,
  IconLaptopCode, IconGraduationCap, IconClinicMedical, IconTruck
} from '../components/ui/icons';

export const OTHER_SECTOR = 'Autre';

// Photos d'illustration (public/assets/media/secteurs), sous licence libre (CC BY-SA 4.0 sauf mention) :
// le crédit (auteur, page d'origine, licence) est obligatoire et s'affiche sous les cartes de secteur.
const photo = (file, author, subject, page, license = 'CC BY-SA 4.0') => ({
  image: `/assets/media/secteurs/${file}.jpg`,
  credit: { author, subject, license, page: `https://commons.wikimedia.org/wiki/File:${page}` }
});

export const SECTORS = [
  { label: 'Agriculture et élevage', icon: IconSeedling, impact: 'Sécurité alimentaire, revenus ruraux',
    ...photo('agriculture', 'Tawaye', 'maraîchage en saison froide au Niger', 'Le_mara%C3%AEchage_pendant_la_saison_froide_au_Niger.jpg') },
  { label: 'Transformation alimentaire', icon: IconIndustry, impact: 'Valeur ajoutée locale, emplois',
    ...photo('transformation', 'Fhadel ALOU', "transformation de l'arachide", 'Transformation_Arrachide_06.jpg') },
  { label: 'Restauration et hôtellerie', icon: IconUtensils, impact: 'Produits locaux, emplois de service',
    ...photo('restauration', 'NigerTZai', 'café-restaurant du musée régional de Dosso', 'Niger,_Dosso_(43),_museum,_caf%C3%A9-restaurant.jpg') },
  { label: 'Artisanat', icon: IconTools, impact: 'Savoir-faire, emploi des femmes',
    ...photo('artisanat', 'Hamzacom97', 'travail du cuir au musée national Boubou Hama, Niamey', 'Artisan_au_mus%C3%A9e_national_Boubou_Hama_de_Niamey_entrain_de_transformer_la_peau_d%27animal.jpg') },
  { label: 'Couture et textile', icon: IconTshirt, impact: 'Habillement local, emploi des jeunes',
    ...photo('couture', 'Ibdawud', 'pagnes tissés zarma (souban)', 'Zarma_Souban_cloth_stripes.jpg', 'CC0') },
  { label: 'Commerce', icon: IconStore, impact: "Accès aux produits, création d'activité",
    ...photo('commerce', 'Tawaye', 'boutique de semences de Dan Saga, région de Maradi', 'La_boutique_de_vente_de_semencier_de_Dan_Saga_%C3%A0_Matadi._01.jpg') },
  { label: 'Services et réparation', icon: IconWrench, impact: 'Métiers de proximité, emploi des jeunes',
    ...photo('services', 'Barke11', 'atelier de soudure à Gaya', 'Atelier_de_soudure_%C3%A0_Gaya.jpg', 'CC BY 4.0') },
  { label: 'Bâtiment et construction', icon: IconHardHat, impact: 'Logements, matériaux et emplois locaux',
    ...photo('batiment', 'NigerTZai', 'fabrication de briques dans le Dallol Bosso', 'Niger,_Filingu%C3%A9_(31),_brick_making.jpg') },
  { label: 'Énergie', icon: IconSolarPanel, impact: 'Électricité propre et abordable',
    ...photo('energie', 'NIGELEC', 'centrale solaire de Malbaza', 'Champs_de_panneaux_solaires_-_Centrale_PV_de_Malbaza.jpg') },
  { label: 'Eau et assainissement', icon: IconTint, impact: "Accès à l'eau potable",
    ...photo('eau', 'Linhuh', 'femmes au puits', 'Les_femmes_au_puits_%C3%A0_eau.jpg') },
  { label: 'Recyclage et environnement', icon: IconRecycle, impact: 'Villes propres, ressources préservées',
    ...photo('environnement', 'Saliousoft', 'pépinière urbaine à Niamey', 'P%C3%A9pini%C3%A8re_dans_la_zone_urbaine_de_la_ville_de_Niamey_au_Niger.jpg') },
  // Photo déjà utilisée par le site (Unsplash, sans crédit obligatoire)
  { label: 'Numérique', icon: IconLaptopCode, impact: 'Innovation, emploi des jeunes',
    image: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80' },
  { label: 'Éducation et formation', icon: IconGraduationCap, impact: 'Compétences, avenir des jeunes',
    ...photo('education', 'severingeo @ Mapillary.com', 'université Abdou Moumouni, Niamey', 'Universit%C3%A9_Abdou_Moumouni_IRSH.jpg') },
  { label: 'Santé', icon: IconClinicMedical, impact: 'Soins de proximité',
    ...photo('sante', 'Barke11', 'hôpital de district de Tchintabaraden', 'H%C3%B4pital_de_District_de_Tchintabaraden_01.jpg') },
  { label: 'Transport', icon: IconTruck, impact: 'Désenclavement, accès aux marchés',
    ...photo('transport', 'NigerTZai', 'route nationale 1, Niger', 'Niger,_Route_Nationale_No.1_(1).jpg') }
];

/** Noms des secteurs, « Autre » en dernier (listes déroulantes) */
export const SECTOR_LABELS = [...SECTORS.map(sector => sector.label), OTHER_SECTOR];

// Anciens noms de catégories (projets enregistrés ou liens publiés avant la liste unique)
const ALIASES = {
  agriculture: 'Agriculture et élevage',
  'agriculture durable': 'Agriculture et élevage',
  elevage: 'Agriculture et élevage',
  aviculture: 'Agriculture et élevage',
  technologie: 'Numérique',
  education: 'Éducation et formation',
  formation: 'Éducation et formation',
  energie: 'Énergie',
  eau: 'Eau et assainissement',
  restauration: 'Restauration et hôtellerie',
  hotellerie: 'Restauration et hôtellerie',
  tourisme: 'Restauration et hôtellerie',
  couture: 'Couture et textile',
  textile: 'Couture et textile',
  mode: 'Couture et textile',
  services: 'Services et réparation',
  mecanique: 'Services et réparation',
  reparation: 'Services et réparation',
  batiment: 'Bâtiment et construction',
  construction: 'Bâtiment et construction',
  btp: 'Bâtiment et construction',
  immobilier: 'Bâtiment et construction',
  environnement: 'Recyclage et environnement',
  recyclage: 'Recyclage et environnement'
};

const fold = (value) => String(value || '').normalize('NFD').replace(/[̀-ͯ]/g, '').trim().toLowerCase();

/** Nom de secteur de la liste unique ; tout nom inconnu ou absent devient « Autre » */
export function normalizeSector(value) {
  const key = fold(value);
  if (!key) return OTHER_SECTOR;
  const exact = SECTOR_LABELS.find(label => fold(label) === key);
  return exact || ALIASES[key] || OTHER_SECTOR;
}

const amountOf = (value) => Math.max(0, Number(value) || 0);

/**
 * Chiffres d'un secteur, calculés à partir des projets publiés.
 * Les rendements sont des rendements VISÉS (non garantis) ; un rendement ou une durée absent est ignoré.
 * @returns {Array} un élément par secteur de SECTORS, dans le même ordre
 */
export function summarizeSectors(projects = []) {
  return SECTORS.map(sector => {
    const own = projects.filter(project => normalizeSector(project.category) === sector.label);
    const open = own.filter(project => {
      const target = amountOf(project.coutTotal);
      return !target || amountOf(project.montantRecu) < target;
    });
    const rates = own.map(project => Number(project.tauxRetour)).filter(rate => rate > 0);
    const durations = own.map(project => parseInt(project.duree, 10)).filter(months => months > 0);
    return {
      ...sector,
      count: own.length,
      openCount: open.length,
      fundedCount: own.length - open.length,
      // Reste à financer sur les projets encore ouverts (jamais négatif)
      remaining: open.reduce((sum, project) => sum + Math.max(0, amountOf(project.coutTotal) - amountOf(project.montantRecu)), 0),
      minReturn: rates.length ? Math.round(Math.min(...rates) * 1000) / 10 : null,
      maxReturn: rates.length ? Math.round(Math.max(...rates) * 1000) / 10 : null,
      minDuration: durations.length ? Math.min(...durations) : null,
      maxDuration: durations.length ? Math.max(...durations) : null,
      risks: [...new Set(own.map(project => project.risque).filter(Boolean))]
    };
  });
}

/** « 8 % » ou « 8 à 10 % » ; « — » si aucune valeur */
export function formatRange(min, max, unit) {
  if (min === null || min === undefined) return '—';
  const text = (value) => Number(value).toLocaleString('fr-FR');
  return min === max ? `${text(min)} ${unit}` : `${text(min)} à ${text(max)} ${unit}`;
}
